/* eslint-disable camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { connect } from 'react-redux';
import '../../../styles/main.scss';
import { envPatch } from '@languages/env_patch';
import { localizedString, getLanguage } from '@languages';
import Language from '@components/Language';

import {
  isWebRTCSupported,
  copyOpenSafari,
  isAndroidDevice,
  isBrowserPermitted,
  setCookie,
  getCookie,
  isShownPrivacy
} from '@lib/Utils';
import APIs from '@services/APIs';
import {
  Privacy,
  Overview,
  Capture,
  VerifyDetails,
  VerifyAddress,
  CaptureOther,
  VerifyOtherCardDetails,
  CaptureNameProof,
  // FaceScan,
  AlternateFlow,
  Success
} from '..';
import { FaceScan } from '../../../_FLOW_V2_FLOW/containers';
import { Error500, InternetCut, DeviceIncompatibleOpenChrome, Timeout } from '../../errors';

import { Modal, LoadingSpinner, LoadingBar, Message } from '../../components';

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      step: 0,
      idType: 'PASSPORT',
      country: process.env.COUNTRY,
      tokenId: '',
      geolocation: '',
      accepted: false,
      cancelled: false,
      confirm: false,
      confirmFR: false,
      error: null,
      tenMinsLeft: false,
      compatible: true,
      completed: false,
      isLandscape: false,
      isProcessing: false,
      isUploading: false,
      uploadBar: 0,
      selfieFR: false,
      webrtc: {
        todo: null,
        status: true
      },
      redirect: false,
      allowedCountries: ['AU', 'NZ'],
      verify: false,
      capturedIds: {},
      showLanguageSelectionPrompt: false
    };

    this.input = null;

    this.handleComplete = this.handleComplete.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleGoBack = this.handleGoBack.bind(this);
    this.handleExit = this.handleExit.bind(this);
  }

  async componentDidMount() {
    const { verify, isCompleted: completed } = this.props;
    this.setState({ verify, completed });

    const { allowedCountries } = this.state;
    const { APP_NAME, COUNTRY } = process.env;
    let { COUNTRIES } = process.env;
    if (!COUNTRIES && envPatch[APP_NAME]) {
      COUNTRIES = envPatch[APP_NAME].COUNTRIES;
    }

    const { status: compatible, todo, os } = await isWebRTCSupported();

    // Init device compatible
    if (window.location.search === '?flow=alternate') {
      // for testing
      this.setState({ compatible, webrtc: { todo: 'ALT_FLOW', status: false } });
    } else {
      this.setState({ compatible, webrtc: { todo, status: compatible } });
    }

    if (os === 'iOS') {
      document.addEventListener(
        'touchmove',
        (event) => {
          if (event.scale !== 1) {
            event.preventDefault();
          }
        },
        { passive: false }
      );
    }

    // Detect if internet cut
    window.addEventListener('offline', () =>
      this.setState({
        error: {
          component: InternetCut,
          props: {}
        }
      })
    );
    // Detect if internet re connected
    window.addEventListener('online', () => {
      this.setState({
        error: {
          component: InternetCut,
          props: {
            isOnline: true,
            onGoBack: this.handleInternetReconnect
          }
        }
      });
    });

    // Set country
    if (COUNTRIES) {
      APIs.country().then(({ country_code }) => {
        const country = allowedCountries.includes(country_code) ? country_code : COUNTRY;
        this.setState({ country });
      });
    } else {
      this.setState({ country: COUNTRY });
    }

    const transToken = document.querySelector('body').getAttribute('data-id');
    const storedTransToken = getCookie('transToken');

    if (transToken === storedTransToken) {
      if (getCookie('_permission') === 1) {
        // Capturing ID
        this.setState({ step: 1, accepted: true });
        setCookie('_permission', 0, -7);
      } else if (getCookie('_permission') === 2) {
        // Liveness step
        this.setState({ step: process.env.COUNTRIES ? 6 : 5, accepted: true });
        setCookie('_permission', 0, -7);
      }
      const FRAttempt = parseInt(getCookie('retryAttempt'), 10) || parseInt(getCookie('retry'), 10);
      if (FRAttempt) {
        this.setState({
          step: 7
        });
      }
    } else {
      setCookie('transToken', transToken, -7);
    }

    // Landscape
    const isLandscape = window.innerHeight < window.innerWidth;
    this.setState({ isLandscape });
    window.addEventListener('orientationchange', () => {
      this.setState(({ isLandscape }) => ({ isLandscape: !isLandscape }));
    });

    // Start the timeout for 1 hour.
    let countdown = 60 * 60;
    const timerId = setInterval(() => {
      if (countdown === 600) {
        this.setState({
          tenMinsLeft: true
        });
      }

      if (countdown <= 0) {
        this.setState({
          tenMinsLeft: false,
          error: {
            component: Timeout,
            props: {}
          }
        });
        clearInterval(timerId);
      }
      countdown--;
    }, 1000);
  }

  /**
   * Unbind the event listener
   */
  componentWillUnmount() {
    window.removeEventListener('orientationchange', () => {
      this.setState(({ isLandscape }) => ({ isLandscape: !isLandscape }));
    });
  }

  /**
   * When all flows have been completed.
   *
   * @param {Object} state
   * @return {Void}
   */
  handleComplete(state) {
    const { sessionId: id, lr, liveness } = state;
    const { REMOVE_ADDRESS_REVIEW = false, WSS_URL } = process.env;
    /**
     * Upload face scan video.
     */
    const data = { id, lr, actions: `Smile, Turn head ${lr ? 'left' : 'right'}` };
    if (liveness) {
      data.success = 1;
    }

    let axiosParams;
    if (WSS_URL.includes('liveness.idkit.io')) {
      this.setState({ isProcessing: true });
    } else {
      this.setState({ uploadBar: 0, isUploading: true });
      axiosParams = {
        before: () => this.setState({ uploadBar: 0, isUploading: true }),
        onProgress: (width) => this.setState({ uploadBar: width })
      };
    }

    APIs.uploadVideo(data, axiosParams, '/api/v4')
      .then(({ status, token, msg: error, action = null }) => {
        this.setState({ isProcessing: false, completed: true, isUploading: false });

        if (status !== 'success') {
          if (action === 'ALTERNATE') {
            this.setState({
              webrtc: { todo: 'ALT_FLOW', status: false },
              step: 7,
              completed: false
            });
            return;
          }
          console.error('video upload failed', { data, error });
          throw new Error(error);
        }

        /**
         * Get face scan results.
         */
        APIs.verifyScans(token)
          .then(({ status, redirectTo }) => {
            if (status !== 'success') {
              console.error('face scan data failed');
              throw new Error(error);
            }

            setCookie('retryAsf', 'no', -10);
            setCookie('retry', null, -7);
            setCookie('retryCaptureWithVideoStream', null, -7);
            setCookie('retryAttempt', null, -7);
            setCookie('idCaptureAttempt', 0, -7);
            setCookie('detailMatch', 'false', -1);
            setCookie('_permission', null, -1);

            if (redirectTo) {
              setTimeout(() => {
                APIs.return();
              }, 3000);
              this.setState({ redirect: true, completed: true });
            } else {
              this.setState({ completed: true });
            }
          })
          .catch(() => {
            const error = {
              component: Error500,
              props: {
                onTryAgain: () => {
                  if (REMOVE_ADDRESS_REVIEW) {
                    this.setState({ step: 4, error: null });
                  } else {
                    this.setState({ step: 6, error: null });
                  }
                }
              }
            };
            this.setState({ error, isProcessing: false, isUploading: false });
          });
      })
      .catch(() => {
        const error = {
          component: Error500,
          props: {
            onTryAgain: () => {
              if (REMOVE_ADDRESS_REVIEW) {
                this.setState({ step: 4, error: null });
              } else {
                this.setState({ step: 6, error: null });
              }
            }
          }
        };
        this.setState({ error, isProcessing: false, isUploading: false });
      });
  }

  /**
   * Handle AlternateFlow complete
   *
   * @param {Object} state
   * @return {Void}
   */
  handleCompleteAlternate = ({ redirectTo }) => {
    setCookie('retryAsf', 'no', -10);
    setCookie('retry', null, -7);
    setCookie('retryCaptureWithVideoStream', null, -7);
    setCookie('retryAttempt', null, -7);
    setCookie('idCaptureAttempt', 0, -7);
    setCookie('detailMatch', 'false', -1);
    setCookie('_permission', null, -1);

    if (redirectTo) {
      setTimeout(() => {
        APIs.return();
      }, 3000);
      this.setState({ redirect: true, completed: true });
    } else {
      this.setState({ completed: true });
    }
  };

  /**
   * Go to next step.
   *
   * @param {Object} state
   * @return {Void}
   */
  handleNextStep(state) {
    let { capturedIds } = this.state;
    if (state && state.idType) {
      capturedIds = {
        [state.idType]: true
      };
    }

    this.setState(({ step }) => ({ step: step + 1, ...state, capturedIds }));
  }

  /**
   * Update the capturedIds
   *
   * @return {Void}
   * @param idType
   * @param oldIdType
   * @param override
   */

  handleNextCaptureIdSelection = (idType, oldIdType = null, override = null) => {
    this.setState(({ capturedIds }) => {
      return {
        capturedIds: {
          ...omit(capturedIds, [oldIdType]),
          [idType]: !(override && override === 'yes')
        }
      };
    });
  };

  /**
   * Handle next for  captureOther
   *
   * @param {Object} state
   * @return {Void}
   */
  handleNextStepCaptureOther = (state) => {
    const { APP_NAME, AUVOI_ONLY_FIRST_ID_OCR = false, AUVOI_REQUIRED_IDS } = process.env;
    let { AUVOI_ID_TYPES } = process.env;
    if (!AUVOI_ID_TYPES && envPatch[APP_NAME]) {
      AUVOI_ID_TYPES = envPatch[APP_NAME].AUVOI_ID_TYPES;
    }

    let { capturedIds } = this.state;
    capturedIds = Object.keys(capturedIds);
    const capturedIdsCount = capturedIds.length;

    const index = AUVOI_ID_TYPES.findIndex(({ ocr, id }) => {
      return id === state.idType && ocr;
    });

    if (AUVOI_ONLY_FIRST_ID_OCR) {
      // NO OCR
      if (
        capturedIdsCount >= AUVOI_REQUIRED_IDS ||
        (capturedIds.includes('PASSPORT') && capturedIds.includes('AUS_AUTO_DRIVERLICENCE'))
      ) {
        this.setState({
          step: 6
        });
      }
    } else if (!AUVOI_ONLY_FIRST_ID_OCR && index > -1) {
      this.setState(({ step }) => ({ step: step + 1, ...state }));
    } else if (
      capturedIdsCount >= AUVOI_REQUIRED_IDS ||
      (capturedIds.includes('PASSPORT') && capturedIds.includes('AUS_AUTO_DRIVERLICENCE'))
    ) {
      this.setState({
        step: 6
      });
    }
  };

  /**
   * Go back to pev step.
   *
   * @return {Void}
   */
  handleGoBack() {
    const { step } = this.state;
    if (step === 0 && isShownPrivacy('AUVOI_FLOW')) {
      this.setState({
        accepted: false,
        selfieFR: false,
        step: 0
      });
    } else {
      this.setState(({ step }) => ({ step: step - 1, selfieFR: false }));
    }
  }

  /**
   * Handle internet reconnection
   *
   * @return {Void}
   */
  handleInternetReconnect = () => {
    this.setState(({ step }) => ({ step: step - 1, error: null }));
  };

  /**
   * Cancel the session.
   *
   * @return {Void}
   */
  handleExit() {
    this.setState({ confirm: false });
    this.handleGoBack();
  }

  handlePostAddressGoBack = () => {
    const { idDetails = {} } = this.props;
    if (idDetails && idDetails.address) {
      this.setState({ step: 2 });
    } else {
      this.handleGoBack();
    }
  };

  handleFRGoBack = () => {
    const reloaded = parseInt(getCookie('retryAttempt'), 10) || parseInt(getCookie('retry'), 10);
    if (reloaded) {
      this.setState({ confirm: true, confirmFR: true });
      return;
    }

    this.handleGoBack();
  };

  renderLanguageSelection() {
    const { showLanguageSelectionPrompt } = this.state;
    if (!showLanguageSelectionPrompt) {
      return null;
    }

    const selectedlanguage = getLanguage();

    return (
      <Language
        selected={selectedlanguage}
        onClose={(selected) => {
          this.setState({
            showLanguageSelectionPrompt: false
          });

          if (document.body.dataset.language === selected) {
            // Do nothing
            return;
          }

          APIs.language(selected).then(() => {
            let currentUrl = window.location.href;
            if (currentUrl.substring(currentUrl.length - 1) !== '/') {
              currentUrl += '/';
            }

            const newUrl = currentUrl + document.body.dataset.id;
            window.location.href = newUrl;
          });
        }}
      />
    );
  }

  /**
   * Render the component's.
   *
   * @return {ReactElement}
   */
  render() {
    const {
      step,
      idType,
      tokenId,
      cancelled,
      confirm,
      confirmFR,
      error,
      tenMinsLeft,
      accepted,
      compatible,
      completed,
      isProcessing,
      isUploading,
      uploadBar,
      isLandscape,
      geolocation,
      country,
      verify,
      modalError,
      webrtc = {},
      nextCapture,
      capturedIds,
      selfieFR,
      redirect
    } = this.state;

    const { MULTI_LANGUAGE } = process.env;
    const { todo, status: isWebRTC } = webrtc;
    const { component: Error, props: errorProps } = error || {};

    const { flowType } = this.props;
    /**
     * Button states
     */
    const confirmBtns = [
      {
        label: localizedString('cancel'),
        onClick: () => this.setState({ confirm: false, confirmFR: false }),
        variant: 'transparent'
      },
      {
        label: localizedString('yesImSure'),
        onClick: () => {
          setCookie('retry', null, -7);
          setCookie('retryAttempt', null, -7);
          this.setState({ step: 1, confirm: false, confirmFR: false, capturedIds: {} });
          // this.handleGoBack();
        }
      }
    ];
    const openChromeBtns = [
      {
        label: localizedString('proceed'),
        full: true,
        onClick: () => {
          const { id } = document.body.dataset;
          document.location = `googlechrome://navigate?url=${document.location.href}${id}`;
          setTimeout(() => {
            this.setState({ compatible: true });
          }, 1000);
        }
      }
    ];
    const openSafariBtns = [
      {
        children: (
          <a className="link-inside-button" href="x-web-search://" target="_self">
            {localizedString('copyLink')}
          </a>
        ),
        full: true,
        onClick: () => copyOpenSafari(this.input)
      }
    ];
    const tenMinsLeftBtns = [
      {
        label: localizedString('ok'),
        onClick: () => this.setState({ tenMinsLeft: false })
      }
    ];
    /**
     * Exceptions for device compatible.
     */
    if (!compatible) {
      if (todo === 'OPEN_CHROME') {
        return <DeviceIncompatibleOpenChrome buttons={openChromeBtns} />;
      }
      if (todo === 'OPEN_SAFARI') {
        return (
          <Message
            title={localizedString('tryNewBrowser')}
            buttons={openSafariBtns}
            issue={localizedString('useSafari')}
          >
            {localizedString('useSafariDesc_Line1')}
            <br />
            <br />
            {localizedString('useSafariDesc_Line2')}
            <input
              readOnly
              className="b-hidden-input"
              ref={(ref) => {
                this.input = ref;
              }}
            />
          </Message>
        );
      }
      if (todo === 'NOTHING_TODO') {
        return (
          <Message
            title={localizedString('tryNewBrowser')}
            issue={localizedString('deviceIncompatibleContent')}
          >
            {localizedString('deviceIncompatibleDesc1')}
            <br />
            <br />
            {localizedString('deviceIncompatibleDesc2')}
          </Message>
        );
      }
      if (todo === 'NEED_ALT_FLOW') {
        const url = `${window.location.href}?flow=alternate`;
        window.location.replace(url);
      }
    }

    // Check if Browser is Firefox, as Firefox is not permitted for mobile flow
    if (isAndroidDevice() && !isBrowserPermitted()) {
      return <DeviceIncompatibleOpenChrome issue={false} buttons={openChromeBtns} />;
    }

    let languageSelectionHandler;
    if (MULTI_LANGUAGE) {
      languageSelectionHandler = () => this.setState({ showLanguageSelectionPrompt: true });
    }

    return (
      <div>
        {modalError && <Modal isOpen {...modalError} />}
        {Error && <Error {...errorProps} />}
        {isLandscape && <Message landscape />}
        {/* Pages */}
        {!accepted && isShownPrivacy('AUVOI_FLOW') && (
          <Privacy
            onGoBack={this.handleGoBack}
            onAccept={() => this.setState({ accepted: true })}
            onSelectLanguage={languageSelectionHandler}
          />
        )}
        {((isShownPrivacy('AUVOI_FLOW') && accepted) || !isShownPrivacy('AUVOI_FLOW')) &&
          !Error &&
          !completed && (
            <div>
              {!step && (
                <Overview
                  onGoBack={this.handleGoBack}
                  onGoNext={() => this.setState({ step: 1 })}
                />
              )}
              {step === 1 && (
                <Capture
                  onGoBack={this.handleGoBack}
                  onNextStep={this.handleNextStep}
                  country={country}
                  nextCapture={nextCapture}
                  onExit={() => this.setState({ confirm: true })}
                />
              )}
              {step === 2 && (
                <VerifyDetails
                  captureBack={(nextCapture = null) => {
                    if (nextCapture) {
                      this.setState({ step: 1, nextCapture });
                    } else {
                      this.setState({ step: 1 });
                    }
                  }}
                  idType={idType}
                  countryCode={country}
                  token={tokenId}
                  verify={verify}
                  flowType={flowType}
                  location={geolocation}
                  onNextStep={(state, skipAddress) => {
                    if (skipAddress) {
                      this.setState({ step: 4 });
                    } else {
                      this.handleNextStep(state);
                    }
                  }}
                  retake={this.handleGoBack}
                  onExit={() => this.setState({ confirm: true })}
                />
              )}
              {step === 3 && (
                <VerifyAddress
                  onNextStep={this.handleNextStep}
                  onGoBack={this.handleGoBack}
                  onExit={() => this.setState({ confirm: true })}
                  location={geolocation}
                  flowType={flowType}
                  countryCode={country}
                />
              )}
              {step === 4 && (
                <CaptureOther
                  capturedIds={capturedIds}
                  onGoBack={this.handlePostAddressGoBack}
                  onNextStep={this.handleNextStepCaptureOther}
                  onIdSelect={this.handleNextCaptureIdSelection}
                  country={country}
                  nextCapture={nextCapture}
                  onExit={() => this.setState({ confirm: true })}
                />
              )}

              {step === 5 && (
                <VerifyOtherCardDetails
                  capturedIds={capturedIds}
                  captureBack={(nextCapture = null) => {
                    if (nextCapture) {
                      this.setState({ step: 4, nextCapture });
                    } else {
                      this.setState({ step: 4 });
                    }
                  }}
                  idType={idType}
                  countryCode={country}
                  token={tokenId}
                  flowType={flowType}
                  onNextStep={(state, uploadNextId) => {
                    if (uploadNextId) {
                      this.setState({ step: 4 });
                    } else {
                      this.handleNextStep(state);
                    }
                  }}
                  retake={this.handleGoBack}
                  onExit={() => this.setState({ confirm: true })}
                />
              )}

              {step === 6 && (
                <CaptureNameProof
                  onGoBack={this.handlePostAddressGoBack}
                  onNextStep={this.handleNextStep}
                  country={country}
                  nextCapture={nextCapture}
                  onExit={() => this.setState({ confirm: true })}
                />
              )}

              {step === 7 && isWebRTC && !selfieFR && (
                <FaceScan
                  idType={idType}
                  tokenId={tokenId}
                  onNextStep={this.handleComplete}
                  onGoBack={this.handleFRGoBack}
                  onSelfie={() => this.setState({ selfieFR: true })}
                  location={geolocation}
                  countryCode={country}
                />
              )}
              {step === 7 && (!isWebRTC || selfieFR) && (
                <AlternateFlow
                  onNextStep={this.handleCompleteAlternate}
                  onGoBack={this.handleFRGoBack}
                />
              )}
            </div>
          )}
        {/* Exceptions */}
        {cancelled && (
          <Message logoFooter cancelled title={localizedString('sessionCancelled')}>
            {localizedString('sessionCancelledDesc1')}
            <br />
            <br />
            {localizedString('sessionCancelledDesc2')}
          </Message>
        )}

        {tenMinsLeft && (
          <Modal isOpen heading={localizedString('tenMinsLeftDesc')} buttons={tenMinsLeftBtns} />
        )}

        {completed && <Success redirect={redirect} />}

        <Modal
          isOpen={confirm}
          heading={localizedString('leaveConfirmation')}
          description=""
          buttons={confirmBtns}
        >
          {confirmFR
            ? localizedString('scanIDAgainPersonalDetails')
            : localizedString('scanIDAgainInformationOnScreen')}
        </Modal>
        {/* End exceptions */}
        {/* Loadings */}
        {isUploading && <LoadingBar heading={localizedString('uploading')} width={uploadBar} />}
        {isProcessing && (
          <LoadingSpinner subtitle="" heading={localizedString('verifyingYourIdentity')} />
        )}

        {this.renderLanguageSelection()}
      </div>
    );
  }
}

App.propTypes = {
  flowType: PropTypes.string,
  verify: PropTypes.bool,
  isCompleted: PropTypes.bool,
  idDetails: PropTypes.object
};

export default connect(mapStateToProps, null)(App);

/**
 * Map the store's state to the component's props
 * @param  {Object} state
 * @return {Object}
 */
function mapStateToProps({ information }) {
  return {
    idDetails: information.idDetails,
    addresses: information.addresses
  };
}
